import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  where,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Bell,
  Briefcase,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Home,
  Loader2,
  LogOut,
  Package,
  Pencil,
  Plus,
  ShoppingCart,
  Save,
  Moon,
  Settings,
  Sun,
  Trophy,
  Trash2,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { auth, db, mirrorDb } from "../firebase";
import mtnLogo from "../assets/networks/mtn.png";
import airteltigoLogo from "../assets/networks/airteltigo.png";
import telecelLogo from "../assets/networks/telecel.png";

const NETWORKS = [
  { name: "MTN", shortLabel: "MTN", logo: mtnLogo },
  { name: "Telecel", shortLabel: "Telecel", logo: telecelLogo },
  { name: "AirtelTigo", shortLabel: "AT", logo: airteltigoLogo },
];

const DEFAULT_NETWORK_STATUS = {
  MTN: { status: "good" },
  AirtelTigo: { status: "good" },
  Telecel: { status: "good" },
};

const DEFAULT_PAYMENT_SETTINGS = {
  paymentNumber: "0545454000",
  paymentName: "KYETECH ADMIN",
};

const TITLE_LOGO_SRC = "/uklogo.png";

const formatGhs = (value) => `GHS ${Number(value || 0).toFixed(2)}`;

const QUERY_LIMIT = 100; // Limit to first 100 records

const getPackageCollection = (network) => {
  switch (network) {
    case "MTN":
      return "packages_MTN";
    case "AirtelTigo":
      return "packages_AirtelTigo";
    case "Telecel":
      return "packages_Telecel";
    default:
      return "packages_MTN";
  }
};

const toMillis = (stamp) => {
  if (!stamp) return 0;
  if (typeof stamp.toDate === "function") return stamp.toDate().getTime();
  if (typeof stamp.seconds === "number") return stamp.seconds * 1000;
  return 0;
};

const getRelativeTime = (timestamp) => {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
};

const normalizePackageKey = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

const buildPackageKey = (packageId, network) => {
  const pkg = String(packageId || "").trim();
  const net = String(network || "").trim();

  if (!pkg) {
    return "";
  }

  if (pkg.includes("_")) {
    return normalizePackageKey(pkg);
  }

  if (net) {
    return normalizePackageKey(`${net}_${pkg}`);
  }

  return normalizePackageKey(pkg);
};

function StatusPill({ status }) {
  const normalized = String(status || "pending").toLowerCase();
  const map = {
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
    disabled: "bg-slate-200 text-slate-700",
    completed: "bg-emerald-100 text-emerald-700",
    processing: "bg-sky-100 text-sky-700",
    pending: "bg-amber-100 text-amber-700",
    reversed: "bg-purple-100 text-purple-700",
    failed: "bg-red-100 text-red-700",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${map[normalized] || "bg-slate-100 text-slate-700"}`}>
      {status || "pending"}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("home");
  const [homeView, setHomeView] = useState("home");
  const [transactionsTab, setTransactionsTab] = useState("purchases");
  const [activeNetwork, setActiveNetwork] = useState("MTN");
  const [userDirectoryView, setUserDirectoryView] = useState("agents");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem("admin-theme") || "light");
  const [summaryRange, setSummaryRange] = useState("month");

  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingRedrawId, setDeletingRedrawId] = useState(null);

  const [packagesActive, setPackagesActive] = useState(true);
  const [mirrorTransactionsEnabled, setMirrorTransactionsEnabled] = useState(true);
  const [acceptNewNumbersEnabled, setAcceptNewNumbersEnabled] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(DEFAULT_NETWORK_STATUS);

  const [packages, setPackages] = useState([]);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [packageForm, setPackageForm] = useState({
    id: "",
    description: "Non-Expiring",
    costPrice: "",
    userPrice: "",
    agentPrice: "",
    outOfStock: false,
  });
  const [showProductForm, setShowProductForm] = useState(false);

  const [users, setUsers] = useState([]);
  const [purchaseTxns, setPurchaseTxns] = useState([]);
  const [walletTxns, setWalletTxns] = useState([]);
  const [productCostByKey, setProductCostByKey] = useState({});
  const [statusDrafts, setStatusDrafts] = useState({});
  const [selectedPurchaseIds, setSelectedPurchaseIds] = useState([]);
  const [selectedTopupRequestIds, setSelectedTopupRequestIds] = useState([]);
  const [selectedWalletTxnIds, setSelectedWalletTxnIds] = useState([]);
  const [bulkPurchaseStatus, setBulkPurchaseStatus] = useState("processing");
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState("all");
  const [purchaseTypeFilter, setPurchaseTypeFilter] = useState("all");
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchasePageSize, setPurchasePageSize] = useState(20);
  const [walletActionAgentId, setWalletActionAgentId] = useState("");
  const [topupRequests, setTopupRequests] = useState([]);
  const [redrawRequests, setRedrawRequests] = useState([]);
  const [redrawSearch, setRedrawSearch] = useState("");
  const [redrawPage, setRedrawPage] = useState(1);
  const [redrawPageSize, setRedrawPageSize] = useState(20);
  const [approvingRedrawId, setApprovingRedrawId] = useState(null);
  const [topupSearch, setTopupSearch] = useState("");
  const [topupPage, setTopupPage] = useState(1);
  const [topupPageSize, setTopupPageSize] = useState(20);
  const [approvingTopupId, setApprovingTopupId] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(DEFAULT_PAYMENT_SETTINGS);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [recentBroadcasts, setRecentBroadcasts] = useState([]);
  const [deletingNotificationId, setDeletingNotificationId] = useState(null);

  const isDark = theme === "dark";
  const sectionCard = `rounded-2xl border p-4 shadow-sm ${
    isDark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-900/15 bg-white"
  }`;
  const mutedText = isDark ? "text-slate-300" : "text-slate-500";
  const panelClass = isDark ? "border-slate-700 bg-slate-800" : "border-slate-900/15 bg-white";
  const inputSurfaceClass = isDark
    ? "border-slate-600 bg-slate-900 text-slate-100"
    : "border-slate-900/20 bg-white text-slate-900";
  const tableWrapClass = isDark ? "border-slate-700" : "border-slate-900/15";

  useEffect(() => {
    localStorage.setItem("admin-theme", theme);
  }, [theme]);

  useEffect(() => {
    const topupQuery = query(collection(db, "topupRequests"), orderBy("createdAt", "desc"), limit(300));

    const unsubscribe = onSnapshot(
      topupQuery,
      (snapshot) => {
        const requests = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => toMillis(b.paidAt || b.createdAt) - toMillis(a.paidAt || a.createdAt));

        setTopupRequests(requests);
      },
      (snapshotError) => {
        setError(snapshotError.message || "Unable to subscribe to topup requests.");
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "redrawRequests"), orderBy("createdAt", "desc"), limit(300));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        setRedrawRequests(rows);
      },
      (err) => {
        console.error("redraw subscription error", err);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    loadSettings();
    loadUsers();
    loadTransactions();
    loadAllProductCosts();
  }, []);

  useEffect(() => {
    const refreshId = window.setInterval(() => {
      loadTransactions();
    }, 30000);

    return () => window.clearInterval(refreshId);
  }, []);

  useEffect(() => {
    loadPackages(activeNetwork);
  }, [activeNetwork]);

  async function loadSettings() {
    const settingsRef = doc(db, "settings", "packages");
    const settingsSnap = await getDoc(settingsRef);

    if (settingsSnap.exists()) {
      const settingsData = settingsSnap.data();
      setPackagesActive(settingsData.packagesActive ?? true);
      setMirrorTransactionsEnabled(settingsData.mirrorTransactionsEnabled ?? true);
      setAcceptNewNumbersEnabled(settingsData.acceptNewNumbersEnabled ?? false);
    } else {
      await setDoc(settingsRef, {
        packagesActive: true,
        mirrorTransactionsEnabled: true,
        acceptNewNumbersEnabled: false,
      });
      setPackagesActive(true);
      setMirrorTransactionsEnabled(true);
      setAcceptNewNumbersEnabled(false);
    }

    const statusRef = doc(db, "network_status", "current");
    const statusSnap = await getDoc(statusRef);

    if (statusSnap.exists()) {
      setNetworkStatus({ ...DEFAULT_NETWORK_STATUS, ...statusSnap.data() });
    } else {
      await setDoc(statusRef, DEFAULT_NETWORK_STATUS);
      setNetworkStatus(DEFAULT_NETWORK_STATUS);
    }

    const paymentRef = doc(db, "settings", "payment");
    const paymentSnap = await getDoc(paymentRef);

    if (paymentSnap.exists()) {
      const paymentData = paymentSnap.data();
      setPaymentSettings({
        paymentNumber: paymentData.paymentNumber || DEFAULT_PAYMENT_SETTINGS.paymentNumber,
        paymentName: paymentData.paymentName || DEFAULT_PAYMENT_SETTINGS.paymentName,
      });
    } else {
      await setDoc(paymentRef, DEFAULT_PAYMENT_SETTINGS);
      setPaymentSettings(DEFAULT_PAYMENT_SETTINGS);
    }
  }

  async function loadPackages(network) {
    const colName = getPackageCollection(network);
    const snap = await getDocs(collection(db, colName));
    const rows = snap.docs.map((item) => {
      const data = item.data();
      const fallbackPrice = Number(data.price || 0);
      return {
        id: item.id,
        ...data,
        userPrice: Number(data.userPrice ?? fallbackPrice),
        agentPrice: Number(data.agentPrice ?? fallbackPrice),
      };
    });

    rows.sort((a, b) => {
      const aNum = Number(String(a.id).match(/\d+/)?.[0] || 0);
      const bNum = Number(String(b.id).match(/\d+/)?.[0] || 0);
      return aNum - bNum;
    });

    setPackages(rows);
  }

  async function loadAllProductCosts() {
    const costMap = {};

    for (const network of NETWORKS) {
      const collectionName = getPackageCollection(network.name);
      const snap = await getDocs(collection(db, collectionName));

      snap.docs.forEach((item) => {
        const data = item.data();
        const key = normalizePackageKey(item.id);
        costMap[key] = Number(data.costPrice || 0);
      });
    }

    setProductCostByKey(costMap);
  }

  async function loadUsers() {
    const snap = await getDocs(collection(db, "users"));
    const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
    rows.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
    setUsers(rows);
  }

  async function loadTransactions() {
    // Fetch with limit
    const purchaseQuery = query(
      collection(db, "credentials"),
      orderBy("assignedAt", "desc"),
      limit(QUERY_LIMIT)
    );
    const walletQuery = query(
      collection(db, "walletTransactions"),
      orderBy("createdAt", "desc"),
      limit(QUERY_LIMIT)
    );

    const [purchaseSnap, walletSnap] = await Promise.all([
      getDocs(purchaseQuery),
      getDocs(walletQuery),
    ]);

    const purchases = purchaseSnap.docs.map((item) => ({
      id: item.id,
      ...item.data(),
    }));

    const wallet = walletSnap.docs.map((item) => ({
      id: item.id,
      ...item.data(),
    }));

    // Back-sync statuses from Kyetech mirror into UK Connect.
    try {
      const purchaseIds = purchases.map((tx) => tx.id).filter(Boolean);
      const mirroredBySourceId = new Map();
      const mirroredByPaystackRef = new Map();

      if (purchaseIds.length) {
        const sourceIdBatches = [];
        for (let i = 0; i < purchaseIds.length; i += 10) {
          sourceIdBatches.push(purchaseIds.slice(i, i + 10));
        }

        const mirrorSnapshots = await Promise.all(
          sourceIdBatches.map((batch) =>
            getDocs(query(collection(mirrorDb, "credentials"), where("sourceDocId", "in", batch), limit(QUERY_LIMIT)))
          )
        );

        mirrorSnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((item) => {
            const row = item.data();
            if (row.sourceDocId) {
              mirroredBySourceId.set(row.sourceDocId, row);
            }
            if (row.paystackRef) {
              mirroredByPaystackRef.set(row.paystackRef, row);
            }
          });
        });
      }

      const updates = [];
      const purchasesWithSyncedStatus = purchases.map((tx) => {
        const mirrorMatch =
          mirroredBySourceId.get(tx.id) ||
          (tx.paystackRef ? mirroredByPaystackRef.get(tx.paystackRef) : null);

        const mirrorStatus = String(mirrorMatch?.status || "").toLowerCase();
        const localStatus = String(tx.status || "pending").toLowerCase();

        if (mirrorStatus && mirrorStatus !== localStatus) {
          updates.push(
            updateDoc(doc(db, "credentials", tx.id), {
              status: mirrorStatus,
              updatedAt: serverTimestamp(),
            })
          );
          return {
            ...tx,
            status: mirrorStatus,
          };
        }

        return tx;
      });

      if (updates.length) {
        await Promise.all(updates);
      }

      setPurchaseTxns(purchasesWithSyncedStatus);
    } catch {
      setPurchaseTxns(purchases);
    }

    setWalletTxns(wallet);
  }

  async function savePackage() {
    setNotice("");
    setError("");

    if (!packageForm.id || !packageForm.userPrice || !packageForm.agentPrice) {
      setError("Enter package ID, user price and agent price.");
      return;
    }

    setLoading(true);
    try {
      const colName = getPackageCollection(activeNetwork);
      const docId = editingPackageId || `${activeNetwork}_${packageForm.id}`;

      await setDoc(doc(db, colName, docId), {
        network: activeNetwork,
        description: packageForm.description,
        costPrice: Number(packageForm.costPrice || 0),
        userPrice: Number(packageForm.userPrice),
        agentPrice: Number(packageForm.agentPrice),
        price: Number(packageForm.userPrice),
        outOfStock: packageForm.outOfStock,
        updatedAt: serverTimestamp(),
      });

      setNotice("Package pricing saved.");
      setEditingPackageId(null);
      setPackageForm({
        id: "",
        description: "Non-Expiring",
        costPrice: "",
        userPrice: "",
        agentPrice: "",
        outOfStock: false,
      });
      setShowProductForm(false);
      await loadPackages(activeNetwork);
      await loadAllProductCosts();
    } catch {
      setError("Unable to save package pricing.");
    } finally {
      setLoading(false);
    }
  }

  function startEditPackage(item) {
    const cleanId = String(item.id || "").replace(`${activeNetwork}_`, "");
    setEditingPackageId(item.id);
    setPackageForm({
      id: cleanId,
      description: item.description || "Non-Expiring",
      costPrice: String(item.costPrice ?? ""),
      userPrice: String(item.userPrice ?? item.price ?? ""),
      agentPrice: String(item.agentPrice ?? item.price ?? ""),
      outOfStock: Boolean(item.outOfStock),
    });
    setShowProductForm(true);
  }

  async function deletePackage(id) {
    if (!window.confirm("Delete this package?")) return;

    const colName = getPackageCollection(activeNetwork);
    await deleteDoc(doc(db, colName, id));
    await loadPackages(activeNetwork);
    await loadAllProductCosts();
  }

  async function toggleUserApproval(userId, approved) {
    await updateDoc(doc(db, "users", userId), {
      approved: !approved,
      updatedAt: serverTimestamp(),
    });
    await loadUsers();
  }

  async function upgradeToAgent(userId, userName) {
    const confirmed = window.confirm(
      `Upgrade "${userName}" to Agent?\n\nThis confirms the user has paid you directly. Their role will be set to Agent (paid & approved).`
    );
    if (!confirmed) return;
    await updateDoc(doc(db, "users", userId), {
      role: "agent",
      paid: true,
      approved: true,
      updatedAt: serverTimestamp(),
    });
    await loadUsers();
  }

  async function adjustAgentWallet(userRecord, mode) {
    setNotice("");
    setError("");

    const isAgent = userRecord.role === "agent" && userRecord.approved === true;
    const userType = isAgent ? "Agent" : "Customer";
    const userName = userRecord.fullName || userRecord.businessName || userRecord.email;

    const rawAmount = window.prompt(
      mode === "credit"
        ? `Enter amount to credit ${userName} (${userType}) (GHS):`
        : `Enter amount to debit ${userName} (${userType}) (GHS):`,
      "0"
    );

    if (rawAmount === null) {
      return;
    }

    const amount = Number(rawAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Enter a valid amount greater than zero.");
      return;
    }

    setLoading(true);
    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", userRecord.id);
        const walletRef = doc(collection(db, "walletTransactions"));
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("Agent account not found.");
        }

        const currentBalance = Number(userSnap.data().walletBalance || 0);
        if (!Number.isFinite(currentBalance)) {
          throw new Error("Agent wallet balance is invalid.");
        }

        const delta = mode === "credit" ? amount : -amount;
        const nextBalance = Number((currentBalance + delta).toFixed(2));

        if (nextBalance < 0) {
          throw new Error("Debit amount exceeds wallet balance.");
        }

        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.set(walletRef, {
          agentId: userRecord.id,
          agentEmail: userRecord.email || "",
          amount: delta,
          balanceAfter: nextBalance,
          type: mode === "credit" ? "admin_credit" : "admin_debit",
          method: "admin",
          status: "completed",
          note: mode === "credit" ? "Admin wallet credit" : "Admin wallet debit",
          createdBy: auth.currentUser?.email || "admin",
          createdAt: serverTimestamp(),
        });
      });

      setNotice(
        mode === "credit"
          ? `Credited ${formatGhs(amount)} to ${userName} (${userType}).`
          : `Debited ${formatGhs(amount)} from ${userName} (${userType}).`
      );
      await loadUsers();
      await loadTransactions();
    } catch (walletError) {
      setError(walletError.message || "Unable to update agent wallet.");
    } finally {
      setLoading(false);
    }
  }

  async function approveTopupRequest(requestId, topupData) {
    setApprovingTopupId(requestId);
    setError("");
    setNotice("");

    try {
      await runTransaction(db, async (transaction) => {
        // Get user record
        const userRef = doc(db, "users", topupData.agentId);
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("Agent account not found.");
        }

        const currentBalance = Number(userSnap.data().walletBalance || 0);
        const amount = Number(topupData.amount || 0);

        if (!Number.isFinite(currentBalance)) {
          throw new Error("Agent wallet balance is invalid.");
        }

        if (!Number.isFinite(amount) || amount <= 0) {
          throw new Error("Invalid topup amount.");
        }

        const nextBalance = Number((currentBalance + amount).toFixed(2));

        // Update user wallet balance
        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        // Create wallet transaction record
        const walletRef = doc(collection(db, "walletTransactions"));
        transaction.set(walletRef, {
          agentId: topupData.agentId,
          agentEmail: topupData.agentEmail || "",
          amount: amount,
          balanceAfter: nextBalance,
          type: "admin_topup_approval",
          method: "manual_momo",
          status: "completed",
          note: `Manual topup approved - Ref: ${topupData.paymentReference}`,
          paymentReference: topupData.paymentReference,
          momoNumber: topupData.momoNumber,
          createdBy: auth.currentUser?.email || "admin",
          createdAt: serverTimestamp(),
        });

        // Update topup request status
        const requestRef = doc(db, "topupRequests", requestId);
        transaction.update(requestRef, {
          status: "approved",
          approvedBy: auth.currentUser?.email || "admin",
          approvedAt: serverTimestamp(),
        });
      });

      setNotice(`Topup of ${formatGhs(topupData.amount)} approved for ${topupData.agentName}.`);
    } catch (approveError) {
      setError(approveError.message || "Unable to approve topup request.");
    } finally {
      setApprovingTopupId(null);
    }
  }

  async function rejectTopupRequest(requestId, topupData) {
    setError("");
    setNotice("");

    try {
      await updateDoc(doc(db, "topupRequests", requestId), {
        status: "rejected",
        rejectedBy: auth.currentUser?.email || "admin",
        rejectedAt: serverTimestamp(),
      });

      setNotice(`Topup request from ${topupData.agentName} rejected.`);
    } catch (rejectError) {
      setError(rejectError.message || "Unable to reject topup request.");
    }
  }

  async function approveRedrawRequest(requestId, redrawData) {
    setApprovingRedrawId(requestId);
    setError("");
    setNotice("");

    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", redrawData.agentId);
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("Agent account not found.");
        }

        const currentBalance = Number(userSnap.data().walletBalance || 0);
        const amount = Number(redrawData.amount || 0);

        if (!Number.isFinite(currentBalance)) {
          throw new Error("Agent wallet balance is invalid.");
        }

        if (!Number.isFinite(amount) || amount <= 0) {
          throw new Error("Invalid redraw amount.");
        }

        if (currentBalance < amount) {
          throw new Error("Agent wallet has insufficient balance for this redraw.");
        }

        const nextBalance = Number((currentBalance - amount).toFixed(2));

        // Deduct from agent wallet
        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        // Record wallet transaction (negative amount)
        const walletRef = doc(collection(db, "walletTransactions"));
        transaction.set(walletRef, {
          agentId: redrawData.agentId,
          agentEmail: redrawData.agentEmail || "",
          amount: -Math.abs(amount),
          balanceBefore: currentBalance,
          balanceAfter: nextBalance,
          type: "redraw",
          method: redrawData.network || redrawData.paymentChannel || "momo",
          status: "completed",
          note: `Redraw approved - Ref: ${requestId}`,
          momoNumber: redrawData.momoNumber || redrawData.paymentNumber || "",
          network: redrawData.network || "",
          createdBy: auth.currentUser?.email || "admin",
          createdAt: serverTimestamp(),
        });

        // Update redraw request status
        const reqRef = doc(db, "redrawRequests", requestId);
        transaction.update(reqRef, {
          status: "approved",
          approvedBy: auth.currentUser?.email || "admin",
          approvedAt: serverTimestamp(),
          processedAt: serverTimestamp(),
        });
      });

      setNotice(`Redraw of ${formatGhs(redrawData.amount)} approved for ${redrawData.agentName || "Agent"}.`);
      await loadUsers();
      await loadTransactions();
    } catch (err) {
      setError(err.message || "Unable to approve redraw request.");
    } finally {
      setApprovingRedrawId(null);
    }
  }

  async function rejectRedrawRequest(requestId, redrawData) {
    setError("");
    setNotice("");

    try {
      await updateDoc(doc(db, "redrawRequests", requestId), {
        status: "rejected",
        rejectedBy: auth.currentUser?.email || "admin",
        rejectedAt: serverTimestamp(),
      });

      setNotice(`Redraw request from ${redrawData.agentName || "Agent"} rejected.`);
    } catch (rejectError) {
      setError(rejectError.message || "Unable to reject redraw request.");
    }
  }

  async function deleteRedrawRequest(requestId, redrawData) {
    setError("");
    setNotice("");

    if (!window.confirm(`Delete redraw request ${requestId.slice(-6)}? This cannot be undone.`)) {
      return;
    }

    setDeletingRedrawId(requestId);
    try {
      await deleteDoc(doc(db, "redrawRequests", requestId));
      setNotice(`Redraw request from ${redrawData.agentName || "Agent"} deleted.`);
    } catch (err) {
      setError(err.message || "Unable to delete redraw request.");
    } finally {
      setDeletingRedrawId(null);
    }
  }

  async function updatePurchaseStatus(txId, nextStatus) {
    const cleanStatus = String(nextStatus || "").toLowerCase();
    if (!cleanStatus) {
      return;
    }

    setNotice("");
    setError("");
    setLoading(true);
    try {
      await updateDoc(doc(db, "credentials", txId), {
        status: cleanStatus,
        updatedAt: serverTimestamp(),
      });

      setPurchaseTxns((prev) =>
        prev.map((row) =>
          row.id === txId
            ? {
                ...row,
                status: cleanStatus,
              }
            : row
        )
      );
      setStatusDrafts((prev) => {
        const next = { ...prev };
        delete next[txId];
        return next;
      });
      setNotice(`Order status updated to ${cleanStatus}.`);
    } catch (statusError) {
      setError(statusError.message || "Unable to update order status.");
    } finally {
      setLoading(false);
    }
  }

  async function togglePackagesAvailability() {
    const next = !packagesActive;
    await updateDoc(doc(db, "settings", "packages"), { packagesActive: next });
    setPackagesActive(next);
  }

  async function toggleMirrorTransactionsAvailability() {
    const next = !mirrorTransactionsEnabled;
    await updateDoc(doc(db, "settings", "packages"), { mirrorTransactionsEnabled: next });
    setMirrorTransactionsEnabled(next);
  }

  async function toggleAcceptNewNumbersAvailability() {
    const next = !acceptNewNumbersEnabled;
    await updateDoc(doc(db, "settings", "packages"), { acceptNewNumbersEnabled: next });
    setAcceptNewNumbersEnabled(next);
  }

  async function updateNetworkAvailability(network, status) {
    await setDoc(
      doc(db, "network_status", "current"),
      {
        [network]: {
          status,
          updatedAt: serverTimestamp(),
        },
      },
      { merge: true }
    );

    setNetworkStatus((prev) => ({
      ...prev,
      [network]: {
        ...(prev[network] || {}),
        status,
      },
    }));
  }

  async function savePaymentSettings() {
    const paymentNumber = String(paymentSettings.paymentNumber || "").trim();
    const paymentName = String(paymentSettings.paymentName || "").trim();

    setNotice("");
    setError("");

    if (!paymentNumber) {
      setError("Enter a valid MoMo number.");
      return;
    }

    if (!paymentName) {
      setError("Enter a valid MoMo account name.");
      return;
    }

    setLoading(true);

    try {
      await setDoc(
        doc(db, "settings", "payment"),
        {
          paymentNumber,
          paymentName,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || "admin",
        },
        { merge: true }
      );

      setPaymentSettings({ paymentNumber, paymentName });
      setNotice("Manual payment settings saved.");
    } catch (saveError) {
      setError(saveError.message || "Unable to save payment settings.");
    } finally {
      setLoading(false);
    }
  }

  async function loadBroadcastNotifications() {
    setLoadingBroadcasts(true);
    try {
      const notificationQuery = query(
        collection(db, "broadcastNotifications"),
        orderBy("createdAt", "desc"),
        limit(20)
      );
      const snap = await getDocs(notificationQuery);
      setRecentBroadcasts(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
    } catch (notificationError) {
      setError(notificationError?.message || "Unable to load notifications.");
    } finally {
      setLoadingBroadcasts(false);
    }
  }

  async function sendBroadcastNotification() {
    const cleanMessage = String(broadcastMessage || "").trim();
    const cleanTitle = String(broadcastTitle || "").trim() || "Admin Update";

    if (!cleanMessage) {
      setError("Enter a notification message before sending.");
      return;
    }

    setSendingBroadcast(true);
    setError("");
    setNotice("");

    try {
      await addDoc(collection(db, "broadcastNotifications"), {
        title: cleanTitle,
        message: cleanMessage,
        target: "all",
        createdBy: auth.currentUser?.email || "admin",
        createdAt: serverTimestamp(),
      });

      setBroadcastTitle("");
      setBroadcastMessage("");
      setNotice("Broadcast notification sent to all users and agents.");
      await loadBroadcastNotifications();
    } catch (broadcastError) {
      setError(broadcastError?.message || "Unable to send broadcast notification.");
    } finally {
      setSendingBroadcast(false);
    }
  }

  async function deleteBroadcastNotification(notificationId) {
    setDeletingNotificationId(notificationId);
    setError("");
    setNotice("");

    try {
      await deleteDoc(doc(db, "broadcastNotifications", notificationId));
      setNotice("Notification deleted successfully.");
      await loadBroadcastNotifications();
    } catch (deleteError) {
      setError(deleteError?.message || "Unable to delete notification.");
    } finally {
      setDeletingNotificationId(null);
    }
  }

  async function logout() {
    await signOut(auth);
    navigate("/admin");
  }

  const escapeCsv = (value) => {
    const text = String(value ?? "");
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  function exportTransactionsCsv() {
    const sourceRows = transactionsTab === "purchases" ? filteredPurchaseTxns : visibleWalletTxns;

    if (!sourceRows.length) {
      setError("No transactions available for export.");
      return;
    }

    const headers =
      transactionsTab === "purchases"
        ? ["Date", "Phone", "Network", "Package", "Amount", "Status"]
        : ["Date", "Agent", "Type", "Method", "Amount", "BalanceAfter", "Reference"];

    const rows =
      transactionsTab === "purchases"
        ? sourceRows.map((tx) => [
            toMillis(tx.assignedAt) ? new Date(toMillis(tx.assignedAt)).toLocaleString() : "",
            tx.assignedTo || "",
            tx.network || "",
            tx.packageId || "",
            Number(tx.totalPaid || 0).toFixed(2),
            tx.status || "pending",
          ])
        : sourceRows.map((tx) => [
            toMillis(tx.createdAt) ? new Date(toMillis(tx.createdAt)).toLocaleString() : "",
            usersById[tx.agentId]?.fullName || usersById[tx.agentId]?.businessName || tx.agentName || tx.agentEmail || "Agent",
            tx.type || "wallet",
            tx.method || "manual",
            Number(tx.amount || 0).toFixed(2),
            Number(tx.balanceAfter || 0).toFixed(2),
            tx.paymentReference || tx.paystackRef || "",
          ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${transactionsTab}-transactions.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setNotice("Transactions exported to CSV.");
  }

  async function bulkUpdatePurchaseStatus() {
    if (!selectedPurchaseIds.length) {
      setError("Select at least one purchase transaction.");
      return;
    }

    const cleanStatus = String(bulkPurchaseStatus || "").toLowerCase();
    if (!cleanStatus) {
      setError("Choose a valid status.");
      return;
    }

    setError("");
    setNotice("");
    setLoading(true);
    try {
      await Promise.all(
        selectedPurchaseIds.map((txId) =>
          updateDoc(doc(db, "credentials", txId), {
            status: cleanStatus,
            updatedAt: serverTimestamp(),
          })
        )
      );

      setPurchaseTxns((prev) =>
        prev.map((tx) =>
          selectedPurchaseIds.includes(tx.id)
            ? {
                ...tx,
                status: cleanStatus,
              }
            : tx
        )
      );
      setSelectedPurchaseIds([]);
      setNotice(`Updated ${selectedPurchaseIds.length} purchase transactions to ${cleanStatus}.`);
    } catch (bulkError) {
      setError(bulkError.message || "Unable to update selected transactions.");
    } finally {
      setLoading(false);
    }
  }

  async function bulkDeletePurchaseTransactions() {
    if (!selectedPurchaseIds.length) {
      setError("Select at least one purchase transaction.");
      return;
    }

    const deleteCount = selectedPurchaseIds.length;
    if (!window.confirm(`Delete ${deleteCount} selected purchase transactions? This cannot be undone.`)) {
      return;
    }

    setError("");
    setNotice("");
    setLoading(true);
    try {
      await Promise.all(selectedPurchaseIds.map((txId) => deleteDoc(doc(db, "credentials", txId))));

      setPurchaseTxns((prev) => prev.filter((tx) => !selectedPurchaseIds.includes(tx.id)));
      setStatusDrafts((prev) => {
        const next = { ...prev };
        selectedPurchaseIds.forEach((txId) => {
          delete next[txId];
        });
        return next;
      });
      setSelectedPurchaseIds([]);
      setNotice(`Deleted ${deleteCount} purchase transactions.`);
    } catch (bulkError) {
      setError(bulkError.message || "Unable to delete selected purchase transactions.");
    } finally {
      setLoading(false);
    }
  }

  async function bulkDeleteTopupRequests() {
    if (!selectedTopupRequestIds.length) {
      setError("Select at least one top-up request.");
      return;
    }

    const deleteCount = selectedTopupRequestIds.length;
    if (!window.confirm(`Delete ${deleteCount} selected top-up requests? This cannot be undone.`)) {
      return;
    }

    setError("");
    setNotice("");
    setLoading(true);
    try {
      await Promise.all(selectedTopupRequestIds.map((requestId) => deleteDoc(doc(db, "topupRequests", requestId))));
      setTopupRequests((prev) => prev.filter((row) => !selectedTopupRequestIds.includes(row.id)));
      setSelectedTopupRequestIds([]);
      setNotice(`Deleted ${deleteCount} top-up requests.`);
    } catch (bulkError) {
      setError(bulkError.message || "Unable to delete selected top-up requests.");
    } finally {
      setLoading(false);
    }
  }

  async function bulkDeleteWalletTransactions() {
    if (!selectedWalletTxnIds.length) {
      setError("Select at least one wallet transaction.");
      return;
    }

    const deleteCount = selectedWalletTxnIds.length;
    if (!window.confirm(`Delete ${deleteCount} selected wallet transactions? This removes logs only and cannot be undone.`)) {
      return;
    }

    setError("");
    setNotice("");
    setLoading(true);
    try {
      await Promise.all(selectedWalletTxnIds.map((txId) => deleteDoc(doc(db, "walletTransactions", txId))));
      setWalletTxns((prev) => prev.filter((tx) => !selectedWalletTxnIds.includes(tx.id)));
      setSelectedWalletTxnIds([]);
      setNotice(`Deleted ${deleteCount} wallet transactions.`);
    } catch (bulkError) {
      setError(bulkError.message || "Unable to delete selected wallet transactions.");
    } finally {
      setLoading(false);
    }
  }

  const stats = useMemo(() => {
    const isAdminRole = (role) => ["admin", "superagent", "super-agent"].includes(String(role || "").toLowerCase());
    const isPaidAgent = (row) => String(row.role || "").toLowerCase() === "agent" && row.paid === true;

    const adminCount = users.filter((u) => u.role === "admin").length;
    const superAgentCount = users.filter((u) => ["superagent", "super-agent"].includes(String(u.role || "").toLowerCase())).length;
    const agentCount = users.filter((u) => u.role === "agent" && u.approved === true).length;
    const customerCount = users.filter((u) => !isAdminRole(u.role) && !isPaidAgent(u)).length;
    const userCount = users.filter((u) => u.role !== "admin").length;
    const pendingOrders = purchaseTxns.filter((t) => (t.status || "").toLowerCase() === "pending").length;

    return { adminCount, superAgentCount, agentCount, customerCount, userCount, pendingOrders };
  }, [users, purchaseTxns]);

  const homeMetrics = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const dayOfWeek = now.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - mondayOffset).getTime();
    const startOfYear = new Date(now.getFullYear(), 0, 1).getTime();

    const rangeStartMap = {
      week: startOfWeek,
      month: startOfMonth,
      year: startOfYear,
      all: 0,
    };
    const activeRangeStart = rangeStartMap[summaryRange] ?? startOfMonth;

    const totalSalesAllTime = purchaseTxns.reduce(
      (sum, tx) => sum + Number(tx.totalPaid || 0),
      0
    );

    const totalSalesToday = purchaseTxns.reduce((sum, tx) => {
      const assignedAtMs = toMillis(tx.assignedAt);
      if (!assignedAtMs || assignedAtMs < startOfToday) {
        return sum;
      }
      return sum + Number(tx.totalPaid || 0);
    }, 0);

    const totalSalesMonth = purchaseTxns.reduce((sum, tx) => {
      const assignedAtMs = toMillis(tx.assignedAt);
      if (!assignedAtMs || assignedAtMs < startOfMonth) {
        return sum;
      }
      return sum + Number(tx.totalPaid || 0);
    }, 0);

    const totalFundsInAgentAccount = users
      .filter((user) => user.role === "agent")
      .reduce((sum, user) => sum + Number(user.walletBalance || 0), 0);

    const totalDeposit = walletTxns
      .filter((tx) => Number(tx.amount || 0) > 0 && String(tx.type || "") !== "purchase")
      .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);

    const totalDebit = walletTxns
      .filter((tx) => Number(tx.amount || 0) < 0)
      .reduce((sum, tx) => sum + Math.abs(Number(tx.amount || 0)), 0);

    const filteredRangePurchases = purchaseTxns.filter((tx) => {
      const assignedAtMs = toMillis(tx.assignedAt);
      return assignedAtMs >= activeRangeStart;
    });

    const rangeSales = filteredRangePurchases.reduce(
      (sum, tx) => sum + Number(tx.totalPaid || 0),
      0
    );

    const rangePaymentsCount = filteredRangePurchases.length;

    const todayPurchases = purchaseTxns.filter((tx) => {
      const assignedAtMs = toMillis(tx.assignedAt);
      return assignedAtMs >= startOfToday;
    });

    const todayTotalOrders = todayPurchases.length;
    const todayCompleted = todayPurchases.filter((tx) => String(tx.status || "").toLowerCase() === "completed").length;
    const todayProcessing = todayPurchases.filter((tx) => String(tx.status || "").toLowerCase() === "processing").length;
    const todayRejected = todayPurchases.filter((tx) => ["rejected", "failed"].includes(String(tx.status || "").toLowerCase())).length;
    const commissionToday = todayPurchases.reduce((sum, tx) => {
      const paidAmount = Number(tx.totalPaid || 0);
      const packageKey = buildPackageKey(tx.packageId, tx.network);
      const productCost = Number(productCostByKey[packageKey] || 0);
      return sum + (paidAmount - productCost);
    }, 0);

    return {
      totalSalesToday,
      totalSalesMonth,
      totalSalesAllTime,
      totalFundsInAgentAccount,
      totalDeposit,
      totalDebit,
      rangeSales,
      rangePaymentsCount,
      todayTotalOrders,
      todayCompleted,
      todayProcessing,
      todayRejected,
      commissionToday,
    };
  }, [purchaseTxns, users, walletTxns, summaryRange, productCostByKey]);

  function exportFinancialSummaryCsv() {
    const rows = [
      ["Range", summaryRange],
      ["Total Payments Received", homeMetrics.rangeSales.toFixed(2)],
      ["Payment Count", String(homeMetrics.rangePaymentsCount)],
      ["Total Funds In Wallets", homeMetrics.totalFundsInAgentAccount.toFixed(2)],
      ["Total Deposit", homeMetrics.totalDeposit.toFixed(2)],
      ["Total Debit", homeMetrics.totalDebit.toFixed(2)],
      ["Today Orders", String(homeMetrics.todayTotalOrders)],
      ["Completed Today", String(homeMetrics.todayCompleted)],
      ["Processing Today", String(homeMetrics.todayProcessing)],
      ["Rejected Today", String(homeMetrics.todayRejected)],
      ["Commission Today", homeMetrics.commissionToday.toFixed(2)],
    ];

    const csv = ["Metric,Value", ...rows.map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "financial-summary.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setNotice("Financial summary exported.");
  }

  const usersById = useMemo(() => {
    return users.reduce((acc, row) => {
      acc[row.id] = row;
      return acc;
    }, {});
  }, [users]);

  const topPerformingAgents = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const dayOfWeek = now.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - mondayOffset).getTime();
    const startOfYear = new Date(now.getFullYear(), 0, 1).getTime();

    const rangeStartMap = {
      week: startOfWeek,
      month: startOfMonth,
      year: startOfYear,
      all: 0,
    };
    const activeRangeStart = rangeStartMap[summaryRange] ?? startOfMonth;

    const grouped = new Map();

    purchaseTxns.forEach((tx) => {
      const assignedAtMs = toMillis(tx.assignedAt);
      if (assignedAtMs < activeRangeStart) return;

      const possibleName =
        tx.assignedBy ||
        tx.agentName ||
        usersById[tx.agentId]?.fullName ||
        usersById[tx.agentId]?.businessName ||
        tx.assignedTo ||
        tx.agentEmail ||
        "Unknown Agent";

      const key = String(tx.agentId || tx.assignedTo || tx.assignedBy || tx.agentEmail || possibleName);
      const row = grouped.get(key) || {
        name: possibleName,
        orders: 0,
        amount: 0,
        completed: 0,
      };

      row.orders += 1;
      row.amount += Number(tx.totalPaid || 0);
      if (String(tx.status || "").toLowerCase() === "completed") {
        row.completed += 1;
      }

      grouped.set(key, row);
    });

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        successRate: row.orders > 0 ? Math.round((row.completed / row.orders) * 100) : 0,
      }))
      .sort((a, b) => {
        if (b.orders !== a.orders) return b.orders - a.orders;
        return b.amount - a.amount;
      })
      .slice(0, 8);
  }, [purchaseTxns, usersById, summaryRange]);

  const visibleWalletTxns = walletTxns.filter((row) =>
    ["topup", "admin_credit", "admin_debit", "admin_manual_setup", "admin_topup_approval"].includes(row.type)
  );
  const purchaseSearchNormalized = String(purchaseSearch || "").trim().toLowerCase();
  const filteredPurchaseTxns = purchaseTxns
    .filter((tx) => {
      const txStatus = String(tx.status || "pending").toLowerCase();
      if (purchaseStatusFilter !== "all" && txStatus !== purchaseStatusFilter) {
        return false;
      }

      const txType = String(tx.type || "data").toLowerCase();
      if (purchaseTypeFilter !== "all" && txType !== purchaseTypeFilter) {
        return false;
      }

      if (!purchaseSearchNormalized) {
        return true;
      }

      const searchable = [
        tx.id,
        tx.assignedTo,
        tx.packageId,
        tx.requestNumber,
        tx.network,
        tx.beneficiary,
        tx.phone,
        tx.phoneNumber,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");

      return searchable.includes(purchaseSearchNormalized);
    });

  const purchaseTotalPages = Math.max(1, Math.ceil(filteredPurchaseTxns.length / purchasePageSize));
  const currentPurchasePage = Math.min(purchasePage, purchaseTotalPages);
  const pagedPurchaseTxns = filteredPurchaseTxns.slice(
    (currentPurchasePage - 1) * purchasePageSize,
    currentPurchasePage * purchasePageSize
  );

  const filteredUsers = useMemo(() => {
    const isAdminRole = (role) => ["admin", "superagent", "super-agent"].includes(String(role || "").toLowerCase());
    const isPaidAgent = (row) => String(row.role || "").toLowerCase() === "agent" && row.paid === true;

    if (userDirectoryView === "office-team") {
      return users.filter((row) => row.role === "admin");
    }

    if (userDirectoryView === "customers") {
      return users.filter((row) => !isAdminRole(row.role) && !isPaidAgent(row));
    }

    return users.filter((row) => row.role === "agent" && row.approved === true);
  }, [users, userDirectoryView]);

  const userDirectoryTitle =
    userDirectoryView === "office-team"
      ? "Office Team"
      : userDirectoryView === "customers"
        ? "Customers"
        : "Agents";

  const userDirectoryDescription =
    userDirectoryView === "office-team"
      ? "Admin accounts only."
      : userDirectoryView === "customers"
        ? "All users who have not paid and activated as agents."
        : "Approved agent accounts.";

  const walletActionAgent = users.find((row) => row.id === walletActionAgentId);
  const topupSearchNormalized = String(topupSearch || "").trim().toLowerCase();
  const filteredTopupRequests = topupRequests.filter((row) => {
    if (!topupSearchNormalized) {
      return true;
    }

    const searchable = [
      row.paymentReference,
      row.agentName,
      row.agentEmail,
      row.momoNumber,
      row.id,
    ]
      .map((value) => String(value || "").toLowerCase())
      .join(" ");

    return searchable.includes(topupSearchNormalized);
  });
  const topupPendingCount = topupRequests.filter((row) => {
    const status = String(row.status || "pending").toLowerCase();
    return ["paid_pending_confirmation", "pending"].includes(status);
  }).length;
  const topupTotalPages = Math.max(1, Math.ceil(filteredTopupRequests.length / topupPageSize));
  const currentTopupPage = Math.min(topupPage, topupTotalPages);
  const pagedTopupRequests = filteredTopupRequests.slice(
    (currentTopupPage - 1) * topupPageSize,
    currentTopupPage * topupPageSize
  );
  const walletRows = visibleWalletTxns.slice(0, 120);
  const topupStartDate = topupRequests.length
    ? new Date(toMillis(topupRequests[topupRequests.length - 1]?.paidAt || topupRequests[topupRequests.length - 1]?.createdAt)).toLocaleDateString()
    : "-";
  const topupEndDate = topupRequests.length
    ? new Date(toMillis(topupRequests[0]?.paidAt || topupRequests[0]?.createdAt)).toLocaleDateString()
    : "-";

  const redrawSearchNormalized = String(redrawSearch || "").trim().toLowerCase();
  const filteredRedrawRequests = redrawRequests.filter((row) => {
    if (!redrawSearchNormalized) return true;
    const searchable = [row.agentName, row.agentEmail, row.momoNumber, row.id]
      .map((v) => String(v || "").toLowerCase())
      .join(" ");
    return searchable.includes(redrawSearchNormalized);
  });

  const redrawPendingCount = redrawRequests.filter((row) => {
    const status = String(row.status || "pending").toLowerCase();
    return ["pending"].includes(status);
  }).length;

  const redrawTotalPages = Math.max(1, Math.ceil(filteredRedrawRequests.length / redrawPageSize));
  const currentRedrawPage = Math.min(redrawPage, redrawTotalPages);
  const pagedRedrawRequests = filteredRedrawRequests.slice(
    (currentRedrawPage - 1) * redrawPageSize,
    currentRedrawPage * redrawPageSize
  );

  const redrawStartDate = redrawRequests.length
    ? new Date(toMillis(redrawRequests[redrawRequests.length - 1]?.createdAt)).toLocaleDateString()
    : "-";
  const redrawEndDate = redrawRequests.length
    ? new Date(toMillis(redrawRequests[0]?.createdAt)).toLocaleDateString()
    : "-";

  const sidebarItems = [
    {
      key: "home",
      label: "Home",
      icon: <Home size={17} />,
      onClick: () => {
        setActiveTab("home");
        setHomeView("home");
      },
    },
    { key: "topup-requests", label: "Top Ups", icon: <AlertCircle size={17} />, onClick: () => setActiveTab("topup-requests") },
    {
      key: "transactions-purchases",
      label: "Purchases",
      icon: <CreditCard size={17} />,
      onClick: () => {
        setActiveTab("transactions");
        setTransactionsTab("purchases");
      },
    },
    {
      key: "transactions-topups",
      label: "Credit/Debit Wallets",
      icon: <Wallet size={17} />,
      onClick: () => {
        setActiveTab("transactions");
        setTransactionsTab("topups");
      },
    },
    {
      key: "users-agents",
      label: "Agents",
      icon: <Users size={17} />,
      onClick: () => {
        setActiveTab("users");
        setUserDirectoryView("agents");
      },
    },
    {
      key: "users-customers",
      label: "Customers",
      icon: <Users size={17} />,
      onClick: () => {
        setActiveTab("users");
        setUserDirectoryView("customers");
      },
    },
    { key: "pricing", label: "Products", icon: <Package size={17} />, onClick: () => setActiveTab("pricing") },
    {
      key: "home-summary",
      label: "Financial Summary",
      icon: <CreditCard size={17} />,
      onClick: () => {
        setActiveTab("home");
        setHomeView("summary");
      },
    },
    {
      key: "office-team",
      label: "Office Team",
      icon: <Briefcase size={17} />,
      onClick: () => {
        setActiveTab("users");
        setUserDirectoryView("office-team");
      },
    },
    { key: "redraw-requests", label: "Redraw Requests", icon: <Wallet size={17} />, onClick: () => setActiveTab("redraw-requests") },
    { key: "settings", label: "Settings", icon: <Settings size={17} />, onClick: () => setActiveTab("settings") },
  ];

  async function editTopupTransaction(tx) {
    setNotice("");
    setError("");

    if (!tx?.agentId) {
      setError("This top-up does not have a valid agent account.");
      return;
    }

    const previousAmount = Number(tx.amount || 0);
    const rawAmount = window.prompt("Enter new top-up amount (GHS):", String(previousAmount));

    if (rawAmount === null) {
      return;
    }

    const newAmount = Number(rawAmount);
    if (!Number.isFinite(newAmount) || newAmount <= 0) {
      setError("Enter a valid top-up amount greater than zero.");
      return;
    }

    setLoading(true);
    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", tx.agentId);
        const walletTxRef = doc(db, "walletTransactions", tx.id);

        const [userSnap, walletTxSnap] = await Promise.all([
          transaction.get(userRef),
          transaction.get(walletTxRef),
        ]);

        if (!userSnap.exists()) {
          throw new Error("Agent account not found.");
        }

        if (!walletTxSnap.exists()) {
          throw new Error("Top-up transaction not found.");
        }

        const currentBalance = Number(userSnap.data().walletBalance || 0);
        if (!Number.isFinite(currentBalance)) {
          throw new Error("Agent wallet balance is invalid.");
        }

        const originalAmount = Number(walletTxSnap.data().amount || 0);
        const delta = Number((newAmount - originalAmount).toFixed(2));
        const nextBalance = Number((currentBalance + delta).toFixed(2));

        if (nextBalance < 0) {
          throw new Error("Updated top-up amount would make this wallet negative.");
        }

        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.update(walletTxRef, {
          amount: newAmount,
          balanceAfter: nextBalance,
          editedBy: auth.currentUser?.email || "admin",
          editedAt: serverTimestamp(),
          note: "Top-up edited by admin",
        });
      });

      setNotice("Top-up updated successfully.");
      await loadUsers();
      await loadTransactions();
    } catch (editError) {
      setError(editError.message || "Unable to edit top-up transaction.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-[#eef1f5] text-slate-900"}`}>
      <div className="flex min-h-screen">
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-slate-950/50 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <aside
          className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r text-white transition-all duration-300 md:static md:flex ${
            sidebarOpen ? "w-64" : "w-0"
          } overflow-hidden border-blue-900/50 bg-gradient-to-b from-[#0a1f66] via-[#0b2373] to-[#08174d]`}
        >
          <div className="border-b border-white/10 p-4">
            <div className="flex items-center gap-2">
              <img src={TITLE_LOGO_SRC} alt="UK Connect logo" className="h-8 w-auto max-w-[150px] object-contain" />
            </div>
          </div>

          <nav className="flex-1 space-y-1 px-2 py-3">
            {sidebarItems.map((item) => {
              const activeByTab =
                (item.key === "transactions-purchases" && activeTab === "transactions" && transactionsTab === "purchases") ||
                (item.key === "transactions-topups" && activeTab === "transactions" && transactionsTab === "topups") ||
                (item.key === "home" && activeTab === "home" && homeView === "home") ||
                (item.key === "users-agents" && activeTab === "users" && userDirectoryView === "agents") ||
                (item.key === "users-customers" && activeTab === "users" && userDirectoryView === "customers") ||
                (item.key === "home-summary" && activeTab === "home" && homeView === "summary") ||
                (item.key === "office-team" && activeTab === "users" && userDirectoryView === "office-team") ||
                item.key === activeTab;

              return (
                <button
                  key={item.key}
                  onClick={item.onClick}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                    activeByTab
                      ? "bg-white text-[#0b2373]"
                      : "text-blue-100 hover:bg-white/10"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="border-t border-white/10 p-3">
            <button
              onClick={logout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold text-red-400 hover:bg-white/10"
            >
              <LogOut size={17} />
              Logout
            </button>
          </div>
        </aside>

        <div className="flex-1">
          <header className={`sticky top-0 z-20 border-b backdrop-blur ${isDark ? "border-slate-700 bg-slate-900/95" : "border-slate-200 bg-[#f7f8fb]/95"}`}>
            <div className="flex items-center justify-between px-4 py-4 md:px-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen((prev) => !prev)}
                  className={`inline-flex rounded-full border p-2 ${isDark ? "border-slate-600 bg-slate-800 text-slate-200" : "border-slate-300 bg-white text-slate-600"}`}
                  aria-label={sidebarOpen ? "Close menu" : "Open menu"}
                >
                  {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                </button>
                <div>
                  <p className={`text-sm font-semibold ${mutedText}`}>Welcome</p>
                  <p className="text-2xl font-black">Dashboard</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTheme(isDark ? "light" : "dark")}
                  className={`rounded-full border p-2 ${isDark ? "border-slate-600 bg-slate-800 text-amber-300" : "border-slate-300 bg-white text-slate-600"}`}
                >
                  {isDark ? <Sun size={16} /> : <Moon size={16} />}
                </button>
                <button
                  onClick={() => {
                    setShowBroadcastModal(true);
                    void loadBroadcastNotifications();
                  }}
                  className={`rounded-full border p-2 ${isDark ? "border-slate-600 bg-slate-800 text-slate-200" : "border-slate-300 bg-white text-slate-600"}`}
                >
                  <Bell size={16} />
                </button>
              </div>
            </div>
          </header>

          <main className="mx-auto w-full max-w-7xl space-y-4 px-3 py-4 md:px-6">
            <div className={`rounded-2xl border p-4 shadow-sm ${panelClass}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className={`text-sm font-semibold ${mutedText}`}>Admin Workspace</p>
                  <div className="flex items-center gap-2">
                    <img src={TITLE_LOGO_SRC} alt="UK Connect" className="h-8 w-auto max-w-[150px] object-contain" />
                    <p className="text-xl font-extrabold">Control Center</p>
                  </div>
                </div>
                {activeTab === "home" && (
                  <div className="hidden gap-2 md:flex">
                    {[
                      { id: "week", label: "Week" },
                      { id: "month", label: "Month" },
                      { id: "year", label: "Year" },
                      { id: "all", label: "All Time" },
                    ].map((range) => (
                      <button
                        key={range.id}
                        onClick={() => setSummaryRange(range.id)}
                        className={`rounded-xl px-3 py-1.5 text-sm font-semibold ${
                          summaryRange === range.id
                            ? "bg-[#2048d8] text-white"
                            : isDark
                              ? "border border-slate-600 bg-slate-800 text-slate-200"
                              : "border border-slate-300 bg-white text-slate-700"
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                    <button
                      onClick={exportFinancialSummaryCsv}
                      className="rounded-xl bg-[#1f5eff] px-3 py-1.5 text-sm font-bold text-white"
                    >
                      Export CSV
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:hidden">
              {[
                { id: "home", label: "Home", icon: <Home size={16} /> },
                { id: "pricing", label: "Products", icon: <Package size={16} /> },
                { id: "users", label: "Users", icon: <Users size={16} /> },
                { id: "topup-requests", label: "Top Ups", icon: <AlertCircle size={16} /> },
                { id: "transactions", label: "Transactions", icon: <CreditCard size={16} /> },
                { id: "settings", label: "Settings", icon: <Settings size={16} /> },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (tab.id === "home") {
                      setHomeView("home");
                    }
                  }}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold ${
                    activeTab === tab.id
                      ? "bg-[#274193] text-white"
                      : isDark
                        ? "border border-slate-700 bg-slate-800 text-slate-200"
                        : "border border-slate-900/15 bg-[#f3f4f7] text-slate-700"
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            {(notice || error) && (
          <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
            {error || notice}
          </div>
        )}

        {activeTab === "home" && homeView === "home" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-black tracking-tight md:text-4xl">Dashboard</h2>
              <p className={`mt-1 text-sm ${mutedText}`}>Real-time insights into agent performance and orders</p>
            </div>

            <section className="space-y-3">
              <h3 className="text-2xl font-black tracking-tight md:text-3xl">User Statistics</h3>

              <div className="grid gap-4 lg:grid-cols-3">
                <div className={`rounded-2xl border p-6 ${isDark ? "border-indigo-700/40 bg-gradient-to-br from-slate-900 to-indigo-950/40" : "border-indigo-300/50 bg-gradient-to-br from-indigo-50 to-white"}`}>
                  <div className="mb-4 flex items-start justify-between">
                    <p className={`text-xs font-bold uppercase tracking-[0.14em] ${mutedText}`}>Total Users</p>
                    <div className={`rounded-xl p-2 ${isDark ? "bg-white/10" : "bg-white"}`}>
                      <Users size={16} />
                    </div>
                  </div>
                  <p className="text-3xl font-black leading-none md:text-4xl">{stats.userCount}</p>
                  <p className={`mt-3 text-sm ${mutedText}`}>
                    {stats.adminCount} admins, {stats.superAgentCount} super agents
                  </p>
                </div>

                <div className={`rounded-2xl border p-6 ${isDark ? "border-emerald-700/40 bg-gradient-to-br from-slate-900 to-emerald-950/40" : "border-emerald-300/50 bg-gradient-to-br from-emerald-50 to-white"}`}>
                  <div className="mb-4 flex items-start justify-between">
                    <p className={`text-xs font-bold uppercase tracking-[0.14em] ${mutedText}`}>Total Agents</p>
                    <div className={`rounded-xl p-2 ${isDark ? "bg-white/10" : "bg-white"}`}>
                      <Users size={16} />
                    </div>
                  </div>
                  <p className="text-3xl font-black leading-none md:text-4xl">{stats.agentCount}</p>
                  <p className={`mt-3 text-sm ${mutedText}`}>Active field agents</p>
                </div>

                <div className={`rounded-2xl border p-6 ${isDark ? "border-sky-700/40 bg-gradient-to-br from-slate-900 to-sky-950/40" : "border-sky-300/50 bg-gradient-to-br from-sky-50 to-white"}`}>
                  <div className="mb-4 flex items-start justify-between">
                    <p className={`text-xs font-bold uppercase tracking-[0.14em] ${mutedText}`}>Total Customers</p>
                    <div className={`rounded-xl p-2 ${isDark ? "bg-white/10" : "bg-white"}`}>
                      <ShoppingCart size={16} />
                    </div>
                  </div>
                  <p className="text-3xl font-black leading-none md:text-4xl">{stats.customerCount}</p>
                  <p className={`mt-3 text-sm ${mutedText}`}>Registered customers</p>
                </div>
              </div>
            </section>

            <section className={`rounded-2xl border p-4 md:p-5 ${isDark ? "border-slate-700 bg-slate-900" : "border-slate-900/15 bg-white"}`}>
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <Trophy size={20} className="text-amber-500" />
                  <h3 className="text-2xl font-black tracking-tight md:text-3xl">Top Performing Agents</h3>
                </div>
                <p className={`mt-1 text-sm ${mutedText}`}>Ranked by total orders completed · {summaryRange === "all" ? "All Time" : summaryRange[0].toUpperCase() + summaryRange.slice(1)}</p>
              </div>

              <div className={`overflow-x-auto rounded-2xl border ${tableWrapClass}`}>
                <table className="min-w-full text-sm">
                  <thead className={isDark ? "bg-slate-900 text-slate-300" : "bg-slate-50 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Rank</th>
                      <th className="px-4 py-3 text-left font-semibold">Agent</th>
                      <th className="px-4 py-3 text-right font-semibold">Orders</th>
                      <th className="px-4 py-3 text-right font-semibold">Amount</th>
                      <th className="px-4 py-3 text-right font-semibold">Success</th>
                    </tr>
                  </thead>
                  <tbody className={isDark ? "bg-slate-800" : "bg-white"}>
                    {topPerformingAgents.map((row, idx) => (
                      <tr key={`${row.name}-${idx}`} className={isDark ? "border-t border-slate-700" : "border-t border-slate-100"}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-black ${idx === 0 ? "bg-amber-400 text-slate-900" : isDark ? "bg-slate-700 text-slate-100" : "bg-slate-100 text-slate-700"}`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-extrabold">{row.name}</p>
                          <p className={`text-xs ${mutedText}`}>{row.orders} total orders</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-black">{row.orders}</p>
                          <p className={`text-xs ${mutedText}`}>completed</p>
                        </td>
                        <td className="px-4 py-3 text-right font-black text-emerald-600">{formatGhs(row.amount)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex rounded-lg px-2.5 py-1 text-xs font-bold ${row.successRate >= 90 ? "bg-emerald-100 text-emerald-700" : row.successRate >= 70 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                            {row.successRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!topPerformingAgents.length && <p className={`mt-4 text-sm ${mutedText}`}>No performance data available in this range yet.</p>}
            </section>
          </div>
        )}

        {activeTab === "home" && homeView === "summary" && (
          <div className="space-y-5">
            <div className={`rounded-2xl border p-5 ${isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <h2 className="text-2xl font-black tracking-tight md:text-3xl">Financial Summary</h2>
              <p className={`${mutedText} mt-1 text-sm md:text-base`}>Overview of payments, wallet balances, and order statistics.</p>
            </div>

            <section className="space-y-3">
              <h3 className="text-xl font-black md:text-2xl">Finance</h3>
              <div className="grid gap-4 lg:grid-cols-2">
                <div className={`rounded-2xl border p-6 shadow-sm ${isDark ? "border-slate-700 bg-gradient-to-br from-slate-800 to-slate-900" : "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${mutedText}`}>Total Payments Received</p>
                  <p className="mt-2 text-3xl font-black md:text-4xl">{formatGhs(homeMetrics.rangeSales)}</p>
                  <p className={`mt-2 text-sm ${mutedText}`}>{homeMetrics.rangePaymentsCount.toLocaleString()} payments</p>
                </div>

                <div className={`rounded-2xl border p-6 shadow-sm ${isDark ? "border-emerald-800 bg-gradient-to-br from-emerald-950 to-slate-900" : "border-emerald-200 bg-gradient-to-br from-emerald-50 to-slate-100"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${mutedText}`}>Total Funds In Wallets</p>
                  <p className="mt-2 text-3xl font-black md:text-4xl">{formatGhs(homeMetrics.totalFundsInAgentAccount)}</p>
                  <p className={`mt-2 text-sm ${mutedText}`}>Deposit: {formatGhs(homeMetrics.totalDeposit)} • Debit: {formatGhs(homeMetrics.totalDebit)}</p>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-xl font-black md:text-2xl">Today</h3>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <div className={`rounded-2xl border p-5 ${isDark ? "border-violet-800 bg-violet-950/40" : "border-violet-200 bg-violet-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.14em] ${mutedText}`}>Today Orders</p>
                  <p className="mt-2 text-2xl font-black md:text-3xl">{homeMetrics.todayTotalOrders}</p>
                </div>
                <div className={`rounded-2xl border p-5 ${isDark ? "border-emerald-800 bg-emerald-950/40" : "border-emerald-200 bg-emerald-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.14em] ${mutedText}`}>Completed Today</p>
                  <p className="mt-2 text-2xl font-black md:text-3xl">{homeMetrics.todayCompleted}</p>
                </div>
                <div className={`rounded-2xl border p-5 ${isDark ? "border-amber-800 bg-amber-950/40" : "border-amber-200 bg-amber-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.14em] ${mutedText}`}>Processing Today</p>
                  <p className="mt-2 text-2xl font-black md:text-3xl">{homeMetrics.todayProcessing}</p>
                </div>
                <div className={`rounded-2xl border p-5 ${isDark ? "border-rose-800 bg-rose-950/40" : "border-rose-200 bg-rose-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.14em] ${mutedText}`}>Rejected Today</p>
                  <p className="mt-2 text-2xl font-black md:text-3xl">{homeMetrics.todayRejected}</p>
                </div>
                <div className={`rounded-2xl border p-5 ${isDark ? "border-cyan-800 bg-cyan-950/40" : "border-cyan-200 bg-cyan-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.14em] ${mutedText}`}>Commission Today</p>
                  <p className="mt-2 text-2xl font-black md:text-3xl">{formatGhs(homeMetrics.commissionToday)}</p>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === "pricing" && (
          <div className="space-y-4">
            <div className={sectionCard}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-3xl font-black tracking-tight">Product Management</p>
                  <p className={`text-sm ${mutedText}`}>Manage cost, customer, and agent pricing by network. No super-agent pricing is included.</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {NETWORKS.map((net) => (
                    <button
                      key={net.name}
                      onClick={() => setActiveNetwork(net.name)}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold ${
                        activeNetwork === net.name
                          ? "border-[#2048d8] bg-[#2048d8] text-white"
                          : isDark
                            ? "border-slate-600 bg-slate-800 text-slate-100"
                            : "border-slate-300 bg-white text-slate-700"
                      }`}
                    >
                      <img src={net.logo} alt={net.name} className="h-4 w-4 object-contain" />
                      {net.shortLabel}
                    </button>
                  ))}

                  <button
                    onClick={() => {
                      setShowProductForm(true);
                      setEditingPackageId(null);
                      setPackageForm({
                        id: "",
                        description: "Non-Expiring",
                        costPrice: "",
                        userPrice: "",
                        agentPrice: "",
                        outOfStock: false,
                      });
                    }}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#1f4bd8] px-4 py-2 text-sm font-bold text-white"
                  >
                    <Plus size={16} />
                    Add New
                  </button>
                </div>
              </div>

              {showProductForm && (
                <div className={`mt-4 rounded-2xl border p-4 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"}`}>
                  <div className="grid gap-3 md:grid-cols-5">
                    <input
                      value={packageForm.id}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, id: e.target.value }))}
                      placeholder="Package"
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    />
                    <input
                      type="number"
                      min="0"
                      value={packageForm.costPrice}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, costPrice: e.target.value }))}
                      placeholder="Cost Price"
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    />
                    <input
                      type="number"
                      min="0"
                      value={packageForm.userPrice}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, userPrice: e.target.value }))}
                      placeholder="Customer Price"
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    />
                    <input
                      type="number"
                      min="0"
                      value={packageForm.agentPrice}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, agentPrice: e.target.value }))}
                      placeholder="Agent Price"
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    />
                    <select
                      value={packageForm.description}
                      onChange={(e) => setPackageForm((prev) => ({ ...prev, description: e.target.value }))}
                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    >
                      <option>Non-Expiring</option>
                      <option>Expiring</option>
                    </select>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <label className={`inline-flex items-center gap-2 text-sm font-semibold ${mutedText}`}>
                      <input
                        type="checkbox"
                        checked={packageForm.outOfStock}
                        onChange={(e) => setPackageForm((prev) => ({ ...prev, outOfStock: e.target.checked }))}
                      />
                      Mark as Out of Stock
                    </label>

                    <div className="flex gap-2">
                      <button
                        onClick={savePackage}
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-xl bg-[#1f4bd8] px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
                      >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        {editingPackageId ? "Update" : "Create"}
                      </button>
                      <button
                        onClick={() => {
                          setShowProductForm(false);
                          setEditingPackageId(null);
                        }}
                        className={`rounded-xl px-4 py-2 text-sm font-bold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className={`mt-4 overflow-x-auto rounded-2xl border ${tableWrapClass}`}>
                <table className="min-w-full text-sm">
                  <thead className={isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Package</th>
                      <th className="px-4 py-3 text-left font-semibold">Cost Price</th>
                      <th className="px-4 py-3 text-left font-semibold">Customer Price</th>
                      <th className="px-4 py-3 text-left font-semibold">Agent Price</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className={isDark ? "bg-slate-800" : "bg-white"}>
                    {packages.map((row) => (
                      <tr key={row.id} className={isDark ? "border-t border-slate-700" : "border-t border-slate-100"}>
                        <td className="px-4 py-3">
                          <p className="font-extrabold">{row.id}</p>
                          <p className={`text-xs ${mutedText}`}>{row.description || "Non-Expiring"}</p>
                        </td>
                        <td className="px-4 py-3 font-semibold">{formatGhs(row.costPrice ?? 0)}</td>
                        <td className="px-4 py-3 font-semibold">{formatGhs(row.userPrice ?? row.price)}</td>
                        <td className="px-4 py-3 font-semibold">{formatGhs(row.agentPrice ?? row.price)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-lg px-2.5 py-1 text-xs font-bold ${row.outOfStock ? "bg-amber-100 text-amber-800" : "bg-black text-white"}`}>
                            {row.outOfStock ? "Out" : "Active"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => startEditPackage(row)}
                              className={`rounded-lg p-2 ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-100 text-slate-700"}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => deletePackage(row.id)}
                              className="rounded-lg bg-red-50 p-2 text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!packages.length && <p className={`px-4 py-4 text-sm ${mutedText}`}>No products found for this network.</p>}
              </div>
            </div>
          </div>
        )}

        {activeTab === "users" && (
          <div className={sectionCard}>
            <p className="text-xl font-extrabold">{userDirectoryTitle}</p>
            <p className={`text-sm ${mutedText}`}>{userDirectoryDescription}</p>

            <div className="mt-3 space-y-2">
              {filteredUsers.map((row) => (
                <div
                  key={row.id}
                  className={`rounded-2xl border p-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-900/15 bg-white"}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-base font-extrabold">{row.fullName || row.businessName || row.email || "Unknown User"}</p>
                      <p className={`text-sm ${mutedText}`}>{row.email || "No email"}</p>
                      <p className={`text-xs ${mutedText}`}>Role: {row.role || "user"} | Wallet: {formatGhs(row.walletBalance || 0)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill status={row.approved === false ? "disabled" : "approved"} />
                      <button
                        onClick={() => toggleUserApproval(row.id, row.approved !== false)}
                        className={`rounded-xl px-3 py-2 text-sm font-bold ${row.approved === false ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
                      >
                        {row.approved === false ? "Approve" : "Disable"}
                      </button>
                      {userDirectoryView === "customers" && (
                        <button
                          onClick={() => upgradeToAgent(row.id, row.fullName || row.email || "this user")}
                          className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-bold text-white hover:bg-amber-600"
                        >
                          Upgrade to Agent
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {!filteredUsers.length && <p className={`text-sm ${mutedText}`}>No records found.</p>}
            </div>
          </div>
        )}

        {activeTab === "topup-requests" && (
          <div className={sectionCard}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-3xl font-black tracking-tight">Topup Requests</p>
                <p className={`text-sm ${mutedText}`}>{topupStartDate} - {topupEndDate} · {topupRequests.length} top-ups loaded</p>
              </div>

              <div className={`rounded-2xl px-4 py-2 text-center ${isDark ? "bg-slate-800" : "bg-white"}`}>
                <p className={`text-xs ${mutedText}`}>Pending</p>
                <p className="text-2xl font-extrabold text-amber-600">{topupPendingCount}</p>
              </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={topupSearch}
                onChange={(event) => {
                  setTopupSearch(event.target.value);
                  setTopupPage(1);
                }}
                placeholder="Reference or name"
                className={`min-w-[240px] rounded-xl border px-3 py-2 text-sm font-semibold outline-none ${inputSurfaceClass}`}
              />
              <button className={`rounded-xl border px-3 py-2 text-sm font-semibold ${isDark ? "border-slate-600 bg-slate-800 text-slate-200" : "border-slate-900/20 bg-white text-slate-700"}`}>
                Columns
              </button>
              <label className={`ml-1 inline-flex items-center gap-2 text-sm font-semibold ${mutedText}`}>
                <input
                  type="checkbox"
                  checked={pagedTopupRequests.length > 0 && pagedTopupRequests.every((req) => selectedTopupRequestIds.includes(req.id))}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setSelectedTopupRequestIds((prev) => [...new Set([...prev, ...pagedTopupRequests.map((req) => req.id)])]);
                    } else {
                      setSelectedTopupRequestIds((prev) => prev.filter((id) => !pagedTopupRequests.some((req) => req.id === id)));
                    }
                  }}
                />
                Select page
              </label>
              <button
                onClick={bulkDeleteTopupRequests}
                disabled={loading || !selectedTopupRequestIds.length}
                className="rounded-xl bg-red-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-60"
              >
                Delete Selected ({selectedTopupRequestIds.length})
              </button>
            </div>

            <div className={`overflow-x-auto rounded-2xl border ${tableWrapClass}`}>
              <table className="min-w-full text-sm">
                <thead className={isDark ? "bg-slate-900 text-slate-300" : "bg-slate-50 text-slate-600"}>
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={pagedTopupRequests.length > 0 && pagedTopupRequests.every((req) => selectedTopupRequestIds.includes(req.id))}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedTopupRequestIds((prev) => [...new Set([...prev, ...pagedTopupRequests.map((req) => req.id)])]);
                          } else {
                            setSelectedTopupRequestIds((prev) => prev.filter((id) => !pagedTopupRequests.some((req) => req.id === id)));
                          }
                        }}
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">Short Reference</th>
                    <th className="px-4 py-3 text-left font-semibold">User</th>
                    <th className="px-4 py-3 text-left font-semibold">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold">Payment</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-right font-semibold">Action</th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-slate-800" : "bg-white"}>
                  {pagedTopupRequests.map((req) => {
                    const reqStatus = String(req.status || "pending").toLowerCase();
                    const isPending = ["paid_pending_confirmation", "pending"].includes(reqStatus);
                    const canApprove = isPending && approvingTopupId !== req.id && !loading;

                    return (
                      <tr key={req.id} className={isDark ? "border-t border-slate-700" : "border-t border-slate-100"}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedTopupRequestIds.includes(req.id)}
                            onChange={(event) => {
                              setSelectedTopupRequestIds((prev) =>
                                event.target.checked
                                  ? [...new Set([...prev, req.id])]
                                  : prev.filter((id) => id !== req.id)
                              );
                            }}
                          />
                        </td>
                        <td className="px-4 py-3 font-semibold">{(req.paymentReference || req.id || "N/A").slice(-6)}</td>
                        <td className="px-4 py-3">
                          <p className="font-extrabold">{req.agentName || "Unknown Agent"}</p>
                          <p className={`text-xs ${mutedText}`}>{req.agentEmail || req.momoNumber || "-"}</p>
                        </td>
                        <td className="px-4 py-3 font-black text-emerald-600">{formatGhs(req.amount || 0)}</td>
                        <td className="px-4 py-3">
                          <p className="inline-flex rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">Cash Out</p>
                          <p className={`mt-1 text-xs ${mutedText}`}>{req.momoNumber || req.paymentReference || "N/A"}</p>
                        </td>
                        <td className="px-4 py-3"><StatusPill status={reqStatus} /></td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              disabled={!canApprove}
                              onClick={() => approveTopupRequest(req.id, req)}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {approvingTopupId === req.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                              Approve
                            </button>
                            <button
                              disabled={!isPending || approvingTopupId === req.id || loading}
                              onClick={() => rejectTopupRequest(req.id, req)}
                              className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <X size={14} />
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className={`text-sm ${mutedText}`}>Page {currentTopupPage} of {topupTotalPages}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTopupPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentTopupPage <= 1}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"} disabled:opacity-40`}
                >
                  Prev
                </button>
                <button
                  onClick={() => setTopupPage((prev) => Math.min(topupTotalPages, prev + 1))}
                  disabled={currentTopupPage >= topupTotalPages}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"} disabled:opacity-40`}
                >
                  Next
                </button>
                <select
                  value={topupPageSize}
                  onChange={(event) => {
                    setTopupPageSize(Number(event.target.value));
                    setTopupPage(1);
                  }}
                  className={`rounded-lg border px-2 py-1.5 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {!filteredTopupRequests.length && (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center">
                <CheckCircle size={24} className="mx-auto mb-2 text-emerald-600" />
                <p className="text-sm font-semibold text-emerald-700">No topup requests found for this filter.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "redraw-requests" && (
          <div className={sectionCard}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-3xl font-black tracking-tight">Redraw Requests</p>
                <p className={`text-sm ${mutedText}`}>{redrawStartDate} - {redrawEndDate} · {redrawRequests.length} requests loaded</p>
              </div>

              <div className={`rounded-2xl px-4 py-2 text-center ${isDark ? "bg-slate-800" : "bg-white"}`}>
                <p className={`text-xs ${mutedText}`}>Pending</p>
                <p className="text-2xl font-extrabold text-amber-600">{redrawPendingCount}</p>
              </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={redrawSearch}
                onChange={(e) => { setRedrawSearch(e.target.value); setRedrawPage(1); }}
                placeholder="Agent name or phone"
                className={`min-w-[240px] rounded-xl border px-3 py-2 text-sm font-semibold outline-none ${inputSurfaceClass}`}
              />
            </div>

            <div className={`overflow-x-auto rounded-2xl border ${tableWrapClass}`}>
              <table className="min-w-full text-sm">
                <thead className={isDark ? "bg-slate-900 text-slate-300" : "bg-slate-50 text-slate-600"}>
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Reference</th>
                    <th className="px-4 py-3 text-left font-semibold">Agent</th>
                    <th className="px-4 py-3 text-left font-semibold">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold">Payment number</th>
                    <th className="px-4 py-3 text-left font-semibold">Payment amount</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-right font-semibold">Action</th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-slate-800" : "bg-white"}>
                  {pagedRedrawRequests.map((r) => {
                    const status = String(r.status || "pending").toLowerCase();
                    const isPending = status === "pending";
                    const canApprove = isPending && approvingRedrawId !== r.id && !loading;

                    return (
                      <tr key={r.id} className={isDark ? "border-t border-slate-700" : "border-t border-slate-100"}>
                        <td className="px-4 py-3 font-semibold">{(r.id || "").slice(-6)}</td>
                        <td className="px-4 py-3">
                          <p className="font-extrabold">{r.agentName || "Unknown"}</p>
                          <p className={`text-xs ${mutedText}`}>{r.agentEmail || r.momoNumber || "-"}</p>
                        </td>
                        <td className="px-4 py-3 font-black text-emerald-600">{formatGhs(r.amount || 0)}</td>
                        <td className="px-4 py-3">
                          <p className={`text-xs ${mutedText}`}>{r.momoNumber || r.paymentNumber || "-"}</p>
                          <div className="mt-2">
                            {r.network ? (
                              <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-2 py-1 text-xs">
                                <img src={
                                  (r.network === 'MTN' && mtnLogo) || (r.network === 'AirtelTigo' && airteltigoLogo) || (r.network === 'Telecel' && telecelLogo) || ''
                                } alt={r.network} className="h-4 w-4 rounded-full" />
                                <span className="font-semibold">{r.network}</span>
                              </div>
                            ) : (
                              <span className={`text-xs ${mutedText}`}>No network</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-black text-emerald-600">{formatGhs(r.paidAmount || r.amount || 0)}</td>
                        <td className="px-4 py-3"><StatusPill status={status} /></td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              disabled={!canApprove}
                              onClick={() => approveRedrawRequest(r.id, r)}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {approvingRedrawId === r.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                              Approve
                            </button>
                            <button
                              disabled={!isPending || approvingRedrawId === r.id || loading}
                              onClick={() => rejectRedrawRequest(r.id, r)}
                              className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <X size={14} />
                              Reject
                            </button>
                            <button
                              disabled={deletingRedrawId === r.id || loading}
                              onClick={() => deleteRedrawRequest(r.id, r)}
                              className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-bold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {deletingRedrawId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className={`text-sm ${mutedText}`}>Page {currentRedrawPage} of {redrawTotalPages}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRedrawPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentRedrawPage <= 1}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"} disabled:opacity-40`}
                >
                  Prev
                </button>
                <button
                  onClick={() => setRedrawPage((prev) => Math.min(redrawTotalPages, prev + 1))}
                  disabled={currentRedrawPage >= redrawTotalPages}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"} disabled:opacity-40`}
                >
                  Next
                </button>
                <select
                  value={redrawPageSize}
                  onChange={(event) => { setRedrawPageSize(Number(event.target.value)); setRedrawPage(1); }}
                  className={`rounded-lg border px-2 py-1.5 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === "transactions" && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => setTransactionsTab("purchases")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold ${
                  transactionsTab === "purchases"
                    ? "bg-[#274193] text-white"
                    : isDark
                      ? "border border-slate-700 bg-slate-800 text-slate-200"
                      : "border border-slate-900/15 bg-[#e8e8ed] text-slate-700"
                }`}
              >
                Purchases
              </button>
              <button
                onClick={() => setTransactionsTab("topups")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold ${
                  transactionsTab === "topups"
                    ? "bg-[#274193] text-white"
                    : isDark
                      ? "border border-slate-700 bg-slate-800 text-slate-200"
                      : "border border-slate-900/15 bg-[#e8e8ed] text-slate-700"
                }`}
              >
                Wallet Activity
              </button>
            </div>

            {transactionsTab === "purchases" && (
              <div className={sectionCard}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-3xl font-black tracking-tight">Product Orders</p>
                    <p className={`text-sm ${mutedText}`}>
                      {filteredPurchaseTxns.length} orders loaded
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={loadTransactions}
                      disabled={loading}
                      className={`rounded-xl px-4 py-2 text-sm font-bold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"}`}
                    >
                      Load More Orders
                    </button>
                    <button
                      onClick={exportTransactionsCsv}
                      className={`rounded-xl border px-4 py-2 text-sm font-bold ${inputSurfaceClass}`}
                    >
                      Export
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={purchaseSearch}
                    onChange={(event) => {
                      setPurchaseSearch(event.target.value);
                      setSelectedPurchaseIds([]);
                      setPurchasePage(1);
                    }}
                    placeholder="Order ID, User, Product"
                    className={`min-w-[240px] rounded-xl border px-3 py-2 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                  />
                  <select
                    value={purchaseStatusFilter}
                    onChange={(event) => {
                      setPurchaseStatusFilter(event.target.value);
                      setSelectedPurchaseIds([]);
                      setPurchasePage(1);
                    }}
                    className={`rounded-xl border px-3 py-2 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                  </select>
                  <select
                    value={purchaseTypeFilter}
                    onChange={(event) => {
                      setPurchaseTypeFilter(event.target.value);
                      setSelectedPurchaseIds([]);
                      setPurchasePage(1);
                    }}
                    className={`rounded-xl border px-3 py-2 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                  >
                    <option value="all">All Types</option>
                    <option value="data">Data</option>
                    <option value="airtime">Airtime</option>
                  </select>
                  <button className={`rounded-xl border px-3 py-2 text-sm font-semibold ${isDark ? "border-slate-600 bg-slate-800 text-slate-200" : "border-slate-300 bg-white text-slate-700"}`}>
                    Columns
                  </button>
                  <label className={`ml-1 inline-flex items-center gap-2 text-sm font-semibold ${mutedText}`}>
                    <input
                      type="checkbox"
                      checked={pagedPurchaseTxns.length > 0 && pagedPurchaseTxns.every((tx) => selectedPurchaseIds.includes(tx.id))}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedPurchaseIds((prev) => [...new Set([...prev, ...pagedPurchaseTxns.map((tx) => tx.id)])]);
                        } else {
                          setSelectedPurchaseIds((prev) => prev.filter((id) => !pagedPurchaseTxns.some((tx) => tx.id === id)));
                        }
                      }}
                    />
                    Select page
                  </label>
                  <select
                    value={bulkPurchaseStatus}
                    onChange={(event) => setBulkPurchaseStatus(event.target.value)}
                    className={`rounded-xl border px-3 py-2 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                  </select>
                  <button
                    onClick={bulkUpdatePurchaseStatus}
                    disabled={loading || !selectedPurchaseIds.length}
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-60"
                  >
                    Apply ({selectedPurchaseIds.length})
                  </button>
                  <button
                    onClick={bulkDeletePurchaseTransactions}
                    disabled={loading || !selectedPurchaseIds.length}
                    className="rounded-xl bg-red-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-60"
                  >
                    Delete ({selectedPurchaseIds.length})
                  </button>
                </div>

                <div className={`mt-4 overflow-x-auto rounded-2xl border ${tableWrapClass}`}>
                  <table className="min-w-full text-sm">
                    <thead className={isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-600"}>
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={pagedPurchaseTxns.length > 0 && pagedPurchaseTxns.every((tx) => selectedPurchaseIds.includes(tx.id))}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setSelectedPurchaseIds((prev) => [...new Set([...prev, ...pagedPurchaseTxns.map((tx) => tx.id)])]);
                              } else {
                                setSelectedPurchaseIds((prev) => prev.filter((id) => !pagedPurchaseTxns.some((tx) => tx.id === id)));
                              }
                            }}
                          />
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">Beneficiary</th>
                        <th className="px-4 py-3 text-left font-semibold">Operator</th>
                        <th className="px-4 py-3 text-left font-semibold">Product</th>
                        <th className="px-4 py-3 text-left font-semibold">Package</th>
                        <th className="px-4 py-3 text-left font-semibold">Amount</th>
                        <th className="px-4 py-3 text-left font-semibold">Status</th>
                        <th className="px-4 py-3 text-left font-semibold">User</th>
                        <th className="px-4 py-3 text-right font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className={isDark ? "bg-slate-800" : "bg-white"}>
                      {pagedPurchaseTxns.map((tx) => (
                        <tr key={tx.id} className={isDark ? "border-t border-slate-700" : "border-t border-slate-100"}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedPurchaseIds.includes(tx.id)}
                              onChange={(event) => {
                                setSelectedPurchaseIds((prev) =>
                                  event.target.checked
                                    ? [...new Set([...prev, tx.id])]
                                    : prev.filter((id) => id !== tx.id)
                                );
                              }}
                            />
                          </td>
                          <td className="px-4 py-3 font-bold">
                            {tx.type === "airtime" ? (
                              <span>📱 {tx.requestNumber || tx.assignedTo || "N/A"}</span>
                            ) : (
                              <span>{tx.beneficiary || tx.assignedTo || "N/A"}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-semibold">{tx.network || "N/A"}</td>
                          <td className="px-4 py-3">{tx.type === "airtime" ? "Airtime" : tx.product || tx.packageId || "Product"}</td>
                          <td className="px-4 py-3 font-semibold">{tx.type === "airtime" ? "Airtime" : tx.packageId || "N/A"}</td>
                          <td className="px-4 py-3 font-black">{formatGhs(tx.totalPaid || 0)}</td>
                          <td className="px-4 py-3"><StatusPill status={tx.status} /></td>
                          <td className="px-4 py-3">
                            <p className="font-bold">{tx.assignedBy || tx.agentName || tx.assignedTo || "Agent"}</p>
                            <p className={`text-xs ${mutedText}`}>{toMillis(tx.assignedAt) ? new Date(toMillis(tx.assignedAt)).toLocaleString() : "Pending"}</p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-2">
                              <select
                                value={statusDrafts[tx.id] || tx.status || "pending"}
                                onChange={(event) =>
                                  setStatusDrafts((prev) => ({
                                    ...prev,
                                    [tx.id]: event.target.value,
                                  }))
                                }
                                className={`rounded-xl border px-2 py-1.5 text-xs font-semibold outline-none ${inputSurfaceClass}`}
                              >
                                <option value="pending">Pending</option>
                                <option value="processing">Processing</option>
                                <option value="completed">Completed</option>
                              </select>
                              <button
                                onClick={() => updatePurchaseStatus(tx.id, statusDrafts[tx.id] || tx.status || "pending")}
                                disabled={loading}
                                className="rounded-xl bg-[#2048d8] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
                              >
                                Save
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <p className={`text-sm ${mutedText}`}>
                    Page {currentPurchasePage} of {purchaseTotalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPurchasePage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPurchasePage <= 1}
                      className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"} disabled:opacity-40`}
                    >
                      Prev
                    </button>
                    <button
                      onClick={() => setPurchasePage((prev) => Math.min(purchaseTotalPages, prev + 1))}
                      disabled={currentPurchasePage >= purchaseTotalPages}
                      className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${isDark ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"} disabled:opacity-40`}
                    >
                      Next
                    </button>
                    <select
                      value={purchasePageSize}
                      onChange={(event) => {
                        const nextSize = Number(event.target.value);
                        setPurchasePageSize(nextSize);
                        setPurchasePage(1);
                      }}
                      className={`rounded-lg border px-2 py-1.5 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {!filteredPurchaseTxns.length && <p className={`mt-4 text-sm ${mutedText}`}>No purchase transactions match this filter.</p>}
              </div>
            )}

            {transactionsTab === "topups" && (
              <div className={sectionCard}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xl font-extrabold">Wallet Transactions</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex flex-col gap-2">
                      <select
                        value={walletActionAgentId}
                        onChange={(event) => setWalletActionAgentId(event.target.value)}
                        className={`rounded-xl border px-3 py-2 text-sm font-semibold outline-none ${inputSurfaceClass}`}
                      >
                        <option value="">Select agent or customer</option>
                        {users
                          .filter((row) => {
                            const isPaidAgent = String(row.role || "").toLowerCase() === "agent" && row.paid === true;
                            const isAdminRole = ["admin", "superagent", "super-agent"].includes(String(row.role || "").toLowerCase());
                            return (row.role === "agent" && row.approved === true) || (!isAdminRole && !isPaidAgent);
                          })
                          .map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.fullName || row.businessName || row.email}
                            </option>
                          ))}
                      </select>
                      {walletActionAgent && (
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                            {walletActionAgent.fullName || walletActionAgent.businessName || walletActionAgent.email}
                          </span>
                          {walletActionAgent.role === "agent" && walletActionAgent.approved === true ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700">
                              ★ Agent
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold text-purple-700">
                              👤 Customer
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (!walletActionAgent) {
                          setError("Select an agent or customer first.");
                          return;
                        }
                        adjustAgentWallet(walletActionAgent, "credit");
                      }}
                      className="rounded-xl bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700"
                    >
                      Credit Account
                    </button>
                    <button
                      onClick={() => {
                        if (!walletActionAgent) {
                          setError("Select an agent or customer first.");
                          return;
                        }
                        adjustAgentWallet(walletActionAgent, "debit");
                      }}
                      className="rounded-xl bg-amber-50 px-3 py-2 text-sm font-bold text-amber-700"
                    >
                      Debit Account
                    </button>
                    <label className={`inline-flex items-center gap-2 text-sm font-semibold ${mutedText}`}>
                      <input
                        type="checkbox"
                        checked={walletRows.length > 0 && walletRows.every((tx) => selectedWalletTxnIds.includes(tx.id))}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedWalletTxnIds((prev) => [...new Set([...prev, ...walletRows.map((tx) => tx.id)])]);
                          } else {
                            setSelectedWalletTxnIds((prev) => prev.filter((id) => !walletRows.some((tx) => tx.id === id)));
                          }
                        }}
                      />
                      Select loaded
                    </label>
                    <button
                      onClick={bulkDeleteWalletTransactions}
                      disabled={loading || !selectedWalletTxnIds.length}
                      className="rounded-xl bg-red-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-60"
                    >
                      Delete Selected ({selectedWalletTxnIds.length})
                    </button>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {walletRows.map((tx) => (
                    <div
                      key={tx.id}
                      className={`rounded-2xl border p-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-900/15 bg-white"}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-extrabold">{usersById[tx.agentId]?.fullName || usersById[tx.agentId]?.businessName || tx.agentName || tx.agentEmail || "Agent"}</p>
                          <p className={`text-sm ${mutedText}`}>{tx.type || "wallet"} • {tx.method || "manual"}</p>
                          {(tx.paystackRef || tx.packageId || tx.beneficiary) && (
                            <p className={`text-xs ${mutedText}`}>{tx.paystackRef || tx.packageId || tx.beneficiary}</p>
                          )}
                          <p className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-600"}`}>Current Balance: {formatGhs(usersById[tx.agentId]?.walletBalance || 0)}</p>
                          <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-400"}`}>{toMillis(tx.createdAt) ? new Date(toMillis(tx.createdAt)).toLocaleString() : "Pending"}</p>
                        </div>
                        <div className="text-right">
                          <label className={`mb-2 inline-flex items-center gap-2 text-xs font-semibold ${mutedText}`}>
                            <input
                              type="checkbox"
                              checked={selectedWalletTxnIds.includes(tx.id)}
                              onChange={(event) => {
                                setSelectedWalletTxnIds((prev) =>
                                  event.target.checked
                                    ? [...new Set([...prev, tx.id])]
                                    : prev.filter((id) => id !== tx.id)
                                );
                              }}
                            />
                            Select
                          </label>
                          <p className={`font-bold ${Number(tx.amount || 0) < 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {Number(tx.amount || 0) < 0 ? "-" : "+"}{formatGhs(Math.abs(Number(tx.amount || 0)))}
                          </p>
                          <StatusPill status="completed" />
                          {["topup", "admin_topup_approval"].includes(tx.type) && (
                            <button
                              onClick={() => editTopupTransaction(tx)}
                              disabled={loading}
                              className="mt-2 rounded-xl bg-[#274193] px-3 py-2 text-xs font-bold text-white disabled:opacity-60"
                            >
                              Edit Top-Up
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!visibleWalletTxns.length && <p className={`text-sm ${mutedText}`}>No wallet transactions yet.</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div className="space-y-4">
            <div className={sectionCard}>
              <p className="text-xl font-extrabold">Appearance</p>
              <p className={`text-sm ${mutedText}`}>Choose the admin portal theme.</p>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <button
                  onClick={() => setTheme("light")}
                  className={`rounded-2xl border p-4 text-left transition ${
                    theme === "light"
                      ? "border-[#274193] bg-[#edf2ff]"
                      : isDark
                        ? "border-slate-700 bg-slate-800"
                        : "border-slate-900/20 bg-white"
                  }`}
                >
                  <div className="mb-2 flex items-center gap-2 font-bold">
                    <Sun size={16} /> Light Mode
                  </div>
                  <p className={`text-sm ${mutedText}`}>Bright workspace for daytime operations.</p>
                </button>

                <button
                  onClick={() => setTheme("dark")}
                  className={`rounded-2xl border p-4 text-left transition ${
                    theme === "dark"
                      ? "border-sky-400 bg-slate-900"
                      : isDark
                        ? "border-slate-700 bg-slate-800"
                        : "border-slate-900/20 bg-white"
                  }`}
                >
                  <div className="mb-2 flex items-center gap-2 font-bold">
                    <Moon size={16} /> Dark Mode
                  </div>
                  <p className={`text-sm ${mutedText}`}>Low-glare workspace for night usage.</p>
                </button>
              </div>
            </div>

            <div className={sectionCard}>
              <p className="text-xl font-extrabold">Package Availability</p>
              <p className={`text-sm ${mutedText}`}>Enable or disable all Kyetech package buying globally.</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className={`rounded-full px-3 py-2 text-sm font-semibold ${packagesActive ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                  {packagesActive ? "Package availability is ON" : "Package availability is OFF"}
                </span>
                <button
                  onClick={togglePackagesAvailability}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 font-bold text-white ${packagesActive ? "bg-red-500" : "bg-emerald-500"}`}
                >
                  {packagesActive ? <ChevronDown size={16} /> : <CheckCircle size={16} />}
                  {packagesActive ? "Set Packages Offline" : "Set Packages Active"}
                </button>
              </div>
            </div>

            <div className={sectionCard}>
              <p className="text-xl font-extrabold">Mirror Transactions</p>
              <p className={`text-sm ${mutedText}`}>When off, orders remain in UK Connect only and are not mirrored to Kyetech Data.</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className={`rounded-full px-3 py-2 text-sm font-semibold ${mirrorTransactionsEnabled ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                  {mirrorTransactionsEnabled ? "Mirror transactions are ON" : "Mirror transactions are OFF"}
                </span>
                <button
                  onClick={toggleMirrorTransactionsAvailability}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 font-bold text-white ${mirrorTransactionsEnabled ? "bg-emerald-500" : "bg-red-500"}`}
                >
                  {mirrorTransactionsEnabled ? <CheckCircle size={16} /> : <ChevronDown size={16} />}
                  {mirrorTransactionsEnabled ? "Mirror Enabled" : "Mirror Disabled"}
                </button>
              </div>
            </div>

            <div className={sectionCard}>
              <p className="text-xl font-extrabold">Accept New Numbers</p>
              <p className={`text-sm ${mutedText}`}>Allow or block data purchases for numbers that are not in ukconnet data.csv.</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className={`rounded-full px-3 py-2 text-sm font-semibold ${acceptNewNumbersEnabled ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                  {acceptNewNumbersEnabled ? "New numbers are accepted" : "New numbers are blocked"}
                </span>
                <button
                  onClick={toggleAcceptNewNumbersAvailability}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 font-bold text-white ${acceptNewNumbersEnabled ? "bg-red-500" : "bg-emerald-500"}`}
                >
                  {acceptNewNumbersEnabled ? <ChevronDown size={16} /> : <CheckCircle size={16} />}
                  {acceptNewNumbersEnabled ? "Stop Accepting New Numbers" : "Accept New Numbers"}
                </button>
              </div>
            </div>

            <div className={sectionCard}>
              <p className="text-xl font-extrabold">Network Status</p>
              <p className={`text-sm ${mutedText}`}>Control what users and agents see for network availability.</p>

              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {NETWORKS.map((n) => {
                  const status = networkStatus?.[n.name]?.status || "good";
                  return (
                    <div
                      key={n.name}
                      className={`rounded-2xl border p-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-900/15 bg-white"}`}
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <img src={n.logo} alt={n.name} className="h-6 w-6 object-contain" />
                        <p className="font-extrabold">{n.name}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: "good", label: "Good", className: "bg-emerald-500" },
                          { value: "slow", label: "Slow", className: "bg-amber-500" },
                          { value: "bad", label: "Bad", className: "bg-red-500" },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => updateNetworkAvailability(n.name, opt.value)}
                            className={`rounded-xl px-2 py-2 text-xs font-bold text-white ${status === opt.value ? opt.className : "bg-slate-300"}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={sectionCard}>
              <p className="text-xl font-extrabold">Manual Payment Details</p>
              <p className={`text-sm ${mutedText}`}>Update the MoMo number and account name shown to agents for manual topups.</p>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label className={`mb-1.5 block text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>MoMo Number</label>
                  <input
                    type="text"
                    value={paymentSettings.paymentNumber}
                    onChange={(event) =>
                      setPaymentSettings((prev) => ({
                        ...prev,
                        paymentNumber: event.target.value,
                      }))
                    }
                    placeholder="0545454000"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none focus:border-[#274193] ${inputSurfaceClass}`}
                  />
                </div>

                <div>
                  <label className={`mb-1.5 block text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>MoMo Name</label>
                  <input
                    type="text"
                    value={paymentSettings.paymentName}
                    onChange={(event) =>
                      setPaymentSettings((prev) => ({
                        ...prev,
                        paymentName: event.target.value,
                      }))
                    }
                    placeholder="KYETECH ADMIN"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none focus:border-[#274193] ${inputSurfaceClass}`}
                  />
                </div>
              </div>

              <div className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-900/15 bg-white"}`}>
                <div>
                  <p className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>Current display</p>
                  <p className={`text-sm ${mutedText}`}>{paymentSettings.paymentNumber} • {paymentSettings.paymentName}</p>
                </div>

                <button
                  onClick={savePaymentSettings}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-2xl bg-[#274193] px-4 py-3 text-sm font-bold text-white disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Payment Details
                </button>
              </div>
            </div>
          </div>
        )}
          </main>

          {showBroadcastModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
              <div className={`w-full max-w-2xl rounded-3xl border p-5 shadow-2xl ${isDark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500">Broadcast</p>
                    <p className="text-xl font-black">Send Notification To All Users</p>
                  </div>
                  <button
                    onClick={() => setShowBroadcastModal(false)}
                    className={`rounded-full border p-2 ${isDark ? "border-slate-600 bg-slate-800" : "border-slate-200 bg-slate-50"}`}
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-3">
                  <input
                    value={broadcastTitle}
                    onChange={(event) => setBroadcastTitle(event.target.value)}
                    placeholder="Title (optional)"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium outline-none transition ${inputSurfaceClass}`}
                  />
                  <textarea
                    value={broadcastMessage}
                    onChange={(event) => setBroadcastMessage(event.target.value)}
                    placeholder="Write a message for all users and agents"
                    rows={4}
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium outline-none transition ${inputSurfaceClass}`}
                  />
                  <button
                    onClick={sendBroadcastNotification}
                    disabled={sendingBroadcast}
                    className="inline-flex items-center rounded-2xl bg-[#2048d8] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60"
                  >
                    {sendingBroadcast ? "Sending..." : "Send Notification"}
                  </button>
                </div>

                <div className="mt-5">
                  <p className={`mb-2 text-sm font-semibold ${mutedText}`}>Recent Broadcasts</p>
                  <div className={`max-h-64 space-y-2 overflow-y-auto rounded-2xl border p-3 ${panelClass}`}>
                    {loadingBroadcasts && <p className={`text-sm ${mutedText}`}>Loading notifications...</p>}
                    {!loadingBroadcasts && recentBroadcasts.length === 0 && (
                      <p className={`text-sm ${mutedText}`}>No broadcast notifications yet.</p>
                    )}
                    {!loadingBroadcasts && recentBroadcasts.map((item) => {
                      const createdTime = toMillis(item.createdAt) ? new Date(toMillis(item.createdAt)) : null;
                      const timeAgo = createdTime ? getRelativeTime(createdTime.getTime()) : "Unknown";
                      
                      return (
                        <div key={item.id} className={`rounded-xl border p-3 flex items-start justify-between gap-2 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold">{item.title || "Admin Update"}</p>
                            <p className={`mt-1 text-sm ${mutedText}`}>{item.message || ""}</p>
                            <p className={`mt-2 text-xs ${mutedText}`}>
                              {timeAgo}
                            </p>
                          </div>
                          <button
                            onClick={() => deleteBroadcastNotification(item.id)}
                            disabled={deletingNotificationId === item.id}
                            className="mt-1 flex-shrink-0 p-1.5 text-red-500 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                            title="Delete notification"
                          >
                            {deletingNotificationId === item.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
